import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { adminApi, errorMessage } from "../../lib/api";
import { Field, inputCls } from "../landing/Field";
import { useRoles } from "../landing/RolePicker";

const parseRoleRates = (text) => Object.fromEntries(text.split("\n").map((l) => l.split("=")).filter((p) => p.length === 2 && p[0].trim() && p[1].trim() !== "").map(([r, a]) => [r.trim(), Number(a.trim())]));
const roleRatesText = (obj) => Object.entries(obj || {}).map(([r, a]) => `${r} = ${a}`).join("\n");

const toList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean).map(Number);

export const PricingDialog = ({ open, onClose }) => {
  const departments = useRoles();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(null);
    adminApi.get("/settings/pricing").then(({ data }) => setF({
      ...data, wallet_options: data.wallet_options.join(", "), commission_rates: data.commission_rates.join(", "),
      role_rates: roleRatesText(data.role_rates),
    })).catch((e) => toast.error(errorMessage(e)));
  }, [open]);

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.put("/admin/settings/pricing", {
        ...f, unlock_fee: Number(f.unlock_fee), placement_fee: Number(f.placement_fee),
        wallet_options: toList(f.wallet_options), commission_rates: toList(f.commission_rates),
        dept_rates: Object.fromEntries(Object.entries(f.dept_rates).filter(([, v]) => v !== "" && v !== null).map(([k, v]) => [k, Number(v)])),
        role_rates: parseRoleRates(f.role_rates),
      });
      toast.success("Pricing saved. The website shows the new amounts now.");
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const input = (k, props) => (
    <Input data-testid={`pricing-input-${k.replace(/_/g, "-")}`} className={inputCls} value={f[k]} disabled={f.mode === "free"}
      onChange={(e) => setF({ ...f, [k]: e.target.value })} {...props} />
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pricing-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-none border-white/10 bg-[#0F172A] text-slate-100">
        <DialogHeader><DialogTitle className="font-heading text-xl">Pricing & fees</DialogTitle></DialogHeader>
        {f && (
          <div className="space-y-5">
            <div className="flex gap-2">
              {[["paid", "Paid"], ["free", "Free"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setF({ ...f, mode: v })} data-testid={`pricing-mode-${v}`}
                  className={`border px-6 py-2.5 text-sm font-semibold ${f.mode === v ? "border-orange-500 bg-orange-500/10 text-orange-300" : "border-white/10 text-slate-400 hover:text-white"}`}>{l}</button>
              ))}
            </div>
            {f.mode === "free" && <p className="text-sm text-emerald-300">Free mode: the website shows no wallet, unlock fee or commission. Your amounts below are kept for later.</p>}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Contact unlock fee (₹)">{input("unlock_fee", { type: "number", min: 0 })}</Field>
              <Field label="Placement fee (₹)" hint="0 = hide">{input("placement_fee", { type: "number", min: 0 })}</Field>
              <Field label="Wallet amounts (₹)" hint="comma separated">{input("wallet_options", { placeholder: "5000, 10000" })}</Field>
              <Field label="Commission per head / month (₹)" hint="comma separated">{input("commission_rates", { placeholder: "500, 750, 1000" })}</Field>
            </div>
            <details data-testid="pricing-role-rates" className="border border-white/10 p-4">
              <summary className="cursor-pointer text-sm font-semibold text-white">Commission by department / role (₹ per head / month)</summary>
              <p className="mt-2 text-xs text-slate-500">Empty = default (first commission amount). A role rate overrides its department rate.</p>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {departments.map((d) => (
                  <Field key={d.value} label={d.label}>
                    <Input type="number" min={0} data-testid={`pricing-dept-${d.value}`} className={inputCls} disabled={f.mode === "free"}
                      value={f.dept_rates[d.value] ?? ""} onChange={(e) => setF({ ...f, dept_rates: { ...f.dept_rates, [d.value]: e.target.value } })} />
                  </Field>
                ))}
              </div>
              <Field label="Role overrides" hint="one per line, e.g. Welder = 800">
                <textarea data-testid="pricing-role-overrides" rows={4} disabled={f.mode === "free"} value={f.role_rates}
                  onChange={(e) => setF({ ...f, role_rates: e.target.value })} className={`${inputCls} h-auto w-full border p-3`} />
              </Field>
            </details>
            <p className="text-xs text-slate-500">These amounts are shown on the website (How it works, demo and commission calculator). Wallet recharges are paid by UPI and verified in Payments.</p>
            <button onClick={save} disabled={busy} data-testid="pricing-save-button"
              className="h-11 bg-orange-500 px-6 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-50">Save pricing</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
